import React, { useMemo, useRef, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Tab,
  Tabs,
  Box,
  Alert,
  Stack,
  IconButton,
  Typography
} from '@mui/material';
import {
  Close as CloseIcon,
  Upload as UploadIcon
} from '@mui/icons-material';
import { apiClient } from '../config/api';
import aiLogger from '../utils/aiLogger';

const SUPPORTED_TEST_CASE_EXTENSIONS = ['.txt', '.md', '.csv', '.xlsx', '.xls'];

const getUnsupportedTestCaseUploadMessage = (fileName) => {
  const suffix = fileName?.includes('.') ? fileName.slice(fileName.lastIndexOf('.')).toLowerCase() : 'unknown';
  return `Unsupported file type "${suffix}". Test case uploads currently support .txt, .md, .csv, .xlsx, and .xls. Convert the file to a supported format, or paste the content as text and try again.`;
};

const TestCaseUploadModal = ({ open, onClose, projectId, onUploadComplete }) => {
  const [uploading, setUploading] = useState(false);
  const [uploadTab, setUploadTab] = useState(0);
  const [uploadTitle, setUploadTitle] = useState('');
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadText, setUploadText] = useState('');
  const [error, setError] = useState('');
  const [progress, setProgress] = useState(0);
  const [progressMessage, setProgressMessage] = useState('Preparing import...');
  const progressTimerRef = useRef(null);
  const messageTimerRef = useRef(null);

  const wittyMessages = useMemo(() => ([
    'Crunching cells and sipping coffee ☕',
    'Detecting headers like a pro 🕵️',
    'Herding rows into neat blocks 🧹',
    'Walking through sheets… mind the formulas 🚶',
    'Making sense of Steps vs Expected 🤹',
    'Taming multi-line cells like noodles 🍜',
    'Zero hallucinations. Pinky promise 🤖',
    'Verbatim mode engaged. No surprises 🔒',
    'Logging every detail for your report 📝',
    'Almost there… sharpening pencils ✏️'
  ]), []);

  const resetForm = () => {
    setUploadTitle('');
    setUploadFile(null);
    setUploadText('');
    setError('');
    setUploadTab(0);
    setProgress(0);
    setProgressMessage('Preparing import...');
    if (progressTimerRef.current) clearInterval(progressTimerRef.current);
    if (messageTimerRef.current) clearInterval(messageTimerRef.current);
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      const lowerName = file.name.toLowerCase();
      const isSupported = SUPPORTED_TEST_CASE_EXTENSIONS.some((extension) => lowerName.endsWith(extension));
      if (!isSupported) {
        setUploadFile(null);
        setError(getUnsupportedTestCaseUploadMessage(file.name));
        event.target.value = '';
        return;
      }

      setUploadFile(file);
      setError('');
      // Auto-set title from filename if not already set
      if (!uploadTitle) {
        const nameWithoutExt = file.name.replace(/\.[^/.]+$/, '');
        setUploadTitle(nameWithoutExt);
      }
    }
  };

  const handleUpload = async () => {
    if (!uploadTitle.trim()) {
      setError('Please enter a test case title');
      return;
    }

    if (uploadTab === 0 && !uploadFile) {
      setError('Please select a file');
      return;
    }

    if (uploadTab === 1 && !uploadText.trim()) {
      setError('Please enter test case content');
      return;
    }

    try {
      setUploading(true);
      setError('');
      setProgress(5);
      setProgressMessage('Uploading file…');

      // Simulated progress + witty messages rotation (until server responds)
      if (progressTimerRef.current) clearInterval(progressTimerRef.current);
      progressTimerRef.current = setInterval(() => {
        setProgress(prev => (prev >= 90 ? 90 : prev + Math.max(1, Math.round((100 - prev) * 0.04))));
      }, 400);

      if (messageTimerRef.current) clearInterval(messageTimerRef.current);
      messageTimerRef.current = setInterval(() => {
        setProgressMessage(prev => {
          const idx = wittyMessages.indexOf(prev);
          return wittyMessages[(idx + 1 + wittyMessages.length) % wittyMessages.length];
        });
      }, 1800);
      
      aiLogger.userInteraction('upload_test_case', null, {
        projectId,
        title: uploadTitle,
        type: uploadTab === 0 ? 'file' : 'text'
      });
      
      let file;
      let format = 'text';
      
      if (uploadTab === 0 && uploadFile) {
        // File upload
        file = uploadFile;
        format = uploadFile.name.split('.').pop().toLowerCase();
      } else {
        // Text upload - include title in the content
        const contentWithTitle = `# ${uploadTitle}\n\n${uploadText}`;
        file = new File([contentWithTitle], `${uploadTitle}.txt`, { type: 'text/plain' });
        format = 'text';
      }
      
      // Route based on format: Excel/CSV/text should create regular Test Cases
      // not Automation test cases when using this modal in Test Cases tab
      const isSpreadsheet = (format === 'xlsx' || format === 'xls' || format === 'csv');
      const result = await apiClient.uploadTestCases(
        projectId,
        file,
        isSpreadsheet ? (format === 'xls' ? 'xlsx' : format) : format,
        uploadTitle
      );
      
      console.log('📤 Upload result:', result);
      setProgress(100);
      setProgressMessage('Import complete');
      
      if (result.success === true) {
        aiLogger.success(`Successfully uploaded test case${isSpreadsheet ? 's' : ''}`);
        // Pass full result to parent so it can show Import Report
        if (onUploadComplete) {
          onUploadComplete(result);
        }
        handleClose();
      } else {
        console.log('❌ Upload failed - result:', result);
        setError(result.message || 'Failed to upload test case');
      }
    } catch (error) {
      aiLogger.error('Failed to upload test case', error);
      setError(error.message || 'Failed to upload test case');
    } finally {
      setUploading(false);
      if (progressTimerRef.current) clearInterval(progressTimerRef.current);
      if (messageTimerRef.current) clearInterval(messageTimerRef.current);
    }
  };

  const canSubmit = uploadTitle.trim() && (uploadTab === 0 ? uploadFile : uploadText.trim());

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <UploadIcon />
            <span>Upload Test Case</span>
          </Box>
          <IconButton onClick={handleClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent>
        <Stack spacing={3} sx={{ mt: 1 }}>
          {/* Title Field */}
          <TextField
            label="Test Case Title"
            value={uploadTitle}
            onChange={(e) => setUploadTitle(e.target.value)}
            placeholder="Enter test case title..."
            fullWidth
            required
            disabled={uploading}
          />

          {/* Upload Method Tabs */}
          <Box>
            <Tabs
              value={uploadTab}
              onChange={(e, newValue) => setUploadTab(newValue)}
              sx={{ borderBottom: 1, borderColor: 'divider' }}
            >
              <Tab label="Upload File" />
              <Tab label="Enter Text" />
            </Tabs>
          </Box>

          {/* File Upload Tab */}
          {uploadTab === 0 && (
            <Stack spacing={2}>
              <Box
                sx={{
                  border: '2px dashed',
                  borderColor: 'primary.main',
                  borderRadius: 2,
                  p: 3,
                  textAlign: 'center',
                  cursor: 'pointer',
                  '&:hover': {
                    backgroundColor: 'action.hover'
                  }
                }}
                onClick={() => document.getElementById('file-input').click()}
              >
                <input
                  id="file-input"
                  type="file"
                  accept=".txt,.md,.csv,.xlsx,.xls"
                  onChange={handleFileChange}
                  style={{ display: 'none' }}
                />
                {uploadFile ? (
                  <Box>
                    <Typography variant="body1" color="success.main">
                      ✓ {uploadFile.name}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Click to change file
                    </Typography>
                  </Box>
                ) : (
                  <Box>
                    <Typography variant="body1">
                      Click to select a file or drag and drop
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Supports: .txt, .md, .csv, .xlsx, .xls
                    </Typography>
                  </Box>
                )}
              </Box>
            </Stack>
          )}

          {/* Text Input Tab */}
          {uploadTab === 1 && (
            <TextField
              label="Test Case Content"
              value={uploadText}
              onChange={(e) => setUploadText(e.target.value)}
              placeholder="Enter your test case steps here...

Example:
1. Navigate to login page
2. Enter username: standard_user
3. Enter password: secret_sauce
4. Click login button
5. Verify inventory page loads"
              minRows={8}
              maxRows={20}
              multiline
              fullWidth
              disabled={uploading}
            />
          )}

          {error && (
            <Alert severity="error">
              {error}
            </Alert>
          )}

          {uploading && (
            <Box sx={{ mt: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                <Typography variant="body2" color="text.secondary">{progressMessage}</Typography>
                <Typography variant="caption" color="text.secondary">{progress}%</Typography>
              </Box>
              <Box sx={{ mb: 1 }}>
                <div style={{ height: 6, width: '100%', background: '#eceff1', borderRadius: 4 }}>
                  <div style={{ height: 6, width: `${progress}%`, background: '#1976d2', borderRadius: 4, transition: 'width 300ms ease' }} />
                </div>
              </Box>
              <Typography variant="caption" color="text.disabled">
                We’ll show a detailed Import Report when this finishes.
              </Typography>
            </Box>
          )}
        </Stack>
      </DialogContent>

      <DialogActions>
        <Button 
          onClick={handleClose} 
          disabled={uploading}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleUpload}
          disabled={!canSubmit || uploading}
          sx={{ 
            fontWeight: 600,
            px: 3
          }}
        >
          {uploading ? 'Uploading...' : (uploadTab === 0 ? 'Upload File' : 'Save Test Case')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TestCaseUploadModal;
